import React, { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { availableDates } from '../utils/availableDates';

export default function Calendar() {
  const [currentMonth, setCurrentMonth] = useState(new Date(2026, 0, 1));

  const monthNames = [
    'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
    'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return {
      daysInMonth,
      startingDayOfWeek: startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1
    };
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const days = [];

  // Empty cells for days before month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(<div key={`empty-${i}`} className="p-1.5 sm:p-2 md:p-3"></div>);
  }

  // Actual days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateStr = date.toISOString().split('T')[0];
    const isAvailable = availableDates.includes(dateStr);

    days.push(
      <div
        key={day}
        className={`p-1.5 sm:p-2 md:p-3 text-center rounded text-sm sm:text-base min-h-[36px] sm:min-h-[40px] flex items-center justify-center ${
          isAvailable
            ? 'bg-green-100 text-green-800 font-semibold'
            : 'bg-stone-200 text-stone-400'
        }`}
      >
        {day}
      </div>
    );
  }

  return (
    <section className="bg-white rounded-lg shadow-md p-4 sm:p-6 md:p-8 mb-8">
      <h2 className="text-2xl sm:text-3xl font-bold text-stone-800 mb-4 flex items-center gap-2">
        <CalendarIcon className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600" />
        Kdy je volno?
      </h2>
      <p className="text-sm sm:text-base text-stone-600 mb-4 sm:mb-6">Tady vidíte orientační volné termíny:</p>

      {/* Month navigation */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <button
          onClick={goToPreviousMonth}
          className="px-2 py-2 sm:px-3 sm:py-2 md:px-4 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg font-semibold transition-all text-sm sm:text-base min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Předchozí měsíc"
        >
          <span className="hidden sm:inline">← Předchozí</span>
          <span className="sm:hidden">←</span>
        </button>
        <div className="text-base sm:text-lg md:text-xl font-bold text-stone-800 text-center flex-shrink-0">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </div>
        <button
          onClick={goToNextMonth}
          className="px-2 py-2 sm:px-3 sm:py-2 md:px-4 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg font-semibold transition-all text-sm sm:text-base min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Další měsíc"
        >
          <span className="hidden sm:inline">Další →</span>
          <span className="sm:hidden">→</span>
        </button>
      </div>

      {/* Calendar grid */}
      <div className="mb-4">
        <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
          {['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'].map(day => (
            <div key={day} className="text-center font-semibold text-stone-600 text-xs sm:text-sm">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 sm:gap-2">{days}</div>
      </div>

      <div className="flex gap-4 mt-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
          <span>Volno</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-stone-200 border border-stone-300 rounded"></div>
          <span>Obsazeno</span>
        </div>
      </div>
    </section>
  );
}
